import { useId, type ReactNode } from 'react';
import { clsx } from '../../../ds/clsx';
import { RequiredMark, TextField } from '../../../ds/components';
import { amountCents, amountText } from './form';
import styles from './SelfDisclosurePublicPage.module.css';

/** Field variants of the public form (Selbstauskunft 03/04): € input, MM/JJJJ input, segmented control, radio cards. */

export interface EuroFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: (() => void) | undefined;
  required?: boolean | undefined;
  hint?: string | undefined;
  error?: string | undefined;
  autoFocus?: boolean | undefined;
}

export function EuroField({
  label,
  value,
  onChange,
  onBlur,
  required,
  hint,
  error,
  autoFocus,
}: EuroFieldProps) {
  return (
    <TextField
      label={label}
      required={required}
      hint={hint}
      error={error}
      value={value}
      inputMode="decimal"
      autoComplete="off"
      autoFocus={autoFocus}
      trailing={<span className={styles.suffix}>€</span>}
      className={styles.field}
      onChange={(event) => onChange(event.target.value)}
      onBlur={() => {
        const cents = amountCents(value);
        if (cents !== undefined && cents !== null) onChange(amountText(cents));
        onBlur?.();
      }}
    />
  );
}

export interface MonthFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: (() => void) | undefined;
  required?: boolean | undefined;
  error?: string | undefined;
  hint?: string | undefined;
}

/** Six typed digits get the slash („042023" → „04/2023"); everything else stays as typed. */
function monthInput(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  return /^\d{6}$/.test(digits) && !raw.includes('/') ? `${digits.slice(0, 2)}/${digits.slice(2)}` : raw;
}

/** „MM/JJJJ" text input; digits are completed with the slash while typing („042023" → „04/2023"). */
export function MonthField({ label, value, onChange, onBlur, required, error, hint }: MonthFieldProps) {
  return (
    <TextField
      label={label}
      required={required}
      error={error}
      hint={hint}
      value={value}
      placeholder="MM/JJJJ"
      inputMode="numeric"
      autoComplete="off"
      className={styles.field}
      onChange={(event) => onChange(monthInput(event.target.value))}
      onBlur={onBlur}
    />
  );
}

export interface InlineMonthInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  invalid?: boolean | undefined;
}

/** The „bis MM/JJJJ" line inside a radio card (Selbstauskunft 03 „Befristet"): a bare month input. */
export function InlineMonthInput({ label, value, onChange, invalid }: InlineMonthInputProps) {
  return (
    <span className={styles.radioInline}>
      bis
      <input
        type="text"
        aria-label={label}
        aria-invalid={invalid ? true : undefined}
        value={value}
        placeholder="MM/JJJJ"
        inputMode="numeric"
        autoComplete="off"
        size={7}
        className={clsx(styles.radioInlineInput, invalid && styles.radioInlineInvalid)}
        onChange={(event) => onChange(monthInput(event.target.value))}
        onClick={(event) => event.stopPropagation()}
      />
    </span>
  );
}

export interface SegmentedFieldProps<T extends string> {
  label: string;
  required?: boolean | undefined;
  options: ReadonlyArray<{ value: T; label: string }>;
  value: T;
  onChange: (value: T) => void;
  error?: string | undefined;
}

/** Segmented control of the form („Beschäftigung", „Aktuelle Wohnsituation"): sunken track, white active segment. */
export function SegmentedField<T extends string>({
  label,
  required,
  options,
  value,
  onChange,
  error,
}: SegmentedFieldProps<T>) {
  const id = useId();
  return (
    <div className={clsx(styles.field, styles.fieldWide)}>
      <span id={id} className={styles.fieldLabel}>
        {label}
        {required ? <RequiredMark /> : null}
      </span>
      <div role="radiogroup" aria-labelledby={id} className={styles.segmented}>
        {options.map((option) => {
          const active = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={active}
              className={clsx(styles.segment, active && styles.segmentActive)}
              onClick={() => onChange(option.value)}
            >
              {option.label}
            </button>
          );
        })}
      </div>
      {error ? (
        <span className={styles.fieldError} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

export interface RadioCardsFieldProps<T extends string> {
  label: string;
  name: string;
  required?: boolean | undefined;
  options: ReadonlyArray<{ value: T; label: string; description?: ReactNode | undefined }>;
  value: T | '';
  onChange: (value: T) => void;
  error?: string | undefined;
}

/** Radio cards of the form („Arbeitsvertrag": Unbefristet / Befristet with the „bis MM/JJJJ" line). */
export function RadioCardsField<T extends string>({
  label,
  name,
  required,
  options,
  value,
  onChange,
  error,
}: RadioCardsFieldProps<T>) {
  const id = useId();
  return (
    <div className={clsx(styles.field, styles.fieldWide)}>
      <span id={id} className={styles.fieldLabel}>
        {label}
        {required ? <RequiredMark /> : null}
      </span>
      <div role="radiogroup" aria-labelledby={id} className={styles.radioCards}>
        {options.map((option) => {
          const checked = option.value === value;
          return (
            <label key={option.value} className={clsx(styles.radioCard, checked && styles.radioCardChecked)}>
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={checked}
                onChange={() => onChange(option.value)}
                className={styles.radioInput}
              />
              <span className={styles.radioDot} aria-hidden="true" />
              <span className={styles.radioText}>
                <span className={styles.radioTitle}>{option.label}</span>
                {option.description ? (
                  <span className={styles.radioDescription}>{option.description}</span>
                ) : null}
              </span>
            </label>
          );
        })}
      </div>
      {error ? (
        <span className={styles.fieldError} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}
